/**
 * 积分模块 API 接口
 *
 * 覆盖 OpenAPI tags：Credits / Dashboard / Fund Transactions / Billing Statements
 */

import { get } from '../client';
import type { ApiResult } from '../types/common';
import type {
  CreditBalance,
  CreditUsageQueryParams,
  CreditTransactionsQueryParams,
  UsageLogListResult,
  CreditTransactionListResult,
  CreditsAppRankingResult,
  CreditsServiceSummaryResult,
  DashboardOverview,
  FundTransactionListResult,
  BillingStatementListQueryParams,
  BillingStatementPage,
} from './credits';

// ==================== 积分余额 ====================

/**
 * 查询积分余额
 *
 * 对应 OpenAPI: GET /credits/balance
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getCreditBalance(): Promise<ApiResult<CreditBalance>> {
  return get<CreditBalance>('/credits/balance', { requiresAuth: true });
}

// ==================== 使用日志 / 积分流水 ====================

/**
 * 分页查询 API 使用日志
 *
 * 对应 OpenAPI: GET /credits/usage
 *
 * - **Offset**：传 `page` + `limit`，响应含 `total`，`nextCursor` 为 null。
 * - **Cursor**（大数据量推荐）：传 `cursor` + `limit`，忽略 `page`，响应 `total` 为 null，`nextCursor` 为下一页游标。
 * - 可选 `appId`（`app_xxx`）、`startAt` / `endAt`（Unix 秒）缩小时间范围。
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getCreditUsage(
  params?: CreditUsageQueryParams
): Promise<ApiResult<UsageLogListResult>> {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        search.set(key, String(value));
      }
    });
  }
  const qs = search.toString();
  const url = qs ? `/credits/usage?${qs}` : '/credits/usage';
  return get<UsageLogListResult>(url, { requiresAuth: true });
}

/**
 * 分页查询积分流水
 *
 * 对应 OpenAPI: GET /credits/transactions
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getCreditTransactions(
  params?: CreditTransactionsQueryParams
): Promise<ApiResult<CreditTransactionListResult>> {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        search.set(key, String(value));
      }
    });
  }
  const qs = search.toString();
  const url = qs ? `/credits/transactions?${qs}` : '/credits/transactions';
  return get<CreditTransactionListResult>(url, { requiresAuth: true });
}

// ==================== 近 30 天用量 ====================

/**
 * 近 30 天 App 用量排行
 *
 * 对应 OpenAPI: GET /credits/app-ranking
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getCreditsAppRanking(): Promise<
  ApiResult<CreditsAppRankingResult>
> {
  return get<CreditsAppRankingResult>('/credits/app-ranking', {
    requiresAuth: true,
  });
}

/**
 * 近 30 天按服务分组调用汇总
 *
 * 对应 OpenAPI: GET /credits/service-summary
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getCreditsServiceSummary(): Promise<
  ApiResult<CreditsServiceSummaryResult>
> {
  return get<CreditsServiceSummaryResult>('/credits/service-summary', {
    requiresAuth: true,
  });
}

// ==================== Dashboard 概览 ====================

/**
 * 获取账单控制台概览
 *
 * 对应 OpenAPI: GET /dashboard/overview
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getDashboardOverview(): Promise<ApiResult<DashboardOverview>> {
  return get<DashboardOverview>('/dashboard/overview', { requiresAuth: true });
}

// ==================== USDT 资金流水 ====================

/**
 * 查询 USDT 资金流水记录（分页）
 *
 * 对应 OpenAPI: GET /fund-transactions
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getFundTransactions(
  params?: { page?: number; limit?: number }
): Promise<ApiResult<FundTransactionListResult>> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const url = qs ? `/fund-transactions?${qs}` : '/fund-transactions';
  return get<FundTransactionListResult>(url, { requiresAuth: true });
}

// ==================== 月度账单 ====================

/**
 * 查询当前用户月度账单（分页）。
 *
 * 对应 OpenAPI: GET /billing/statements
 *
 * @param params.page 页码（可选，从 1 开始）
 * @param params.limit 每页数量（可选，1-100）
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getBillingStatements(
  params?: BillingStatementListQueryParams
): Promise<ApiResult<BillingStatementPage>> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const url = qs ? `/billing/statements?${qs}` : '/billing/statements';
  return get<BillingStatementPage>(url, { requiresAuth: true });
}
